import { OverlayModule } from '@angular/cdk/overlay';
import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeIt from '@angular/common/locales/it';
import { ErrorHandler, LOCALE_ID, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NavigationError, provideRouter, Router } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbDatepickerI18n, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { filter } from 'rxjs';

import { reloadOnChunkLoadError } from '@services/chunk-load-recovery';
import { GlobalErrorHandler } from '@services/error-handler.service';
import { ItalianDatepickerI18n } from '@services/italian-datepicker-i18n';
import { ItalianPaginatorIntl } from '@services/italian-paginator-intl';
import { SESSION_RESETTABLE } from '@services/session-resettable';

import { AgentsService } from './agents/agents.service';
import { AppRoutes } from './app.routing';
import { NgbDateCustomParserFormatter } from './dateformat';
import { errorInterceptor } from './interceptors/error.interceptor';
import { authInterceptor } from './interceptors/interceptor';
import { ProductsService } from './products/products.service';
import { LoadingService } from './services/loading.service';
import { ListStateService } from './services/list-state.service';
import { NotificationStore } from './services/notification-store.service';
import type { ApplicationConfig } from '@angular/core';

registerLocaleData(localeIt, 'it');
registerLocaleData(localeIt, 'it-IT');
registerLocaleData(localeIt, 'it');

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(AppRoutes),
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor])),
    importProvidersFrom(BrowserAnimationsModule),
    importProvidersFrom(OverlayModule),
    importProvidersFrom(NgbModule),

    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideAppInitializer(() => {
      const router = inject(Router);
      router.events
        .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
        .subscribe((event) => {
          reloadOnChunkLoadError(event.error);
        });
    }),
    { provide: LOCALE_ID, useValue: 'it-IT' },
    { provide: MatPaginatorIntl, useClass: ItalianPaginatorIntl },
    { provide: NgbDatepickerI18n, useClass: ItalianDatepickerI18n },
    { provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter },

    // Session-scoped in-memory state cleared on logout / 401
    { provide: SESSION_RESETTABLE, useExisting: AgentsService, multi: true },
    { provide: SESSION_RESETTABLE, useExisting: ProductsService, multi: true },
    { provide: SESSION_RESETTABLE, useExisting: ListStateService, multi: true },
    { provide: SESSION_RESETTABLE, useExisting: NotificationStore, multi: true },
    { provide: SESSION_RESETTABLE, useExisting: LoadingService, multi: true },
  ],
};
